// ChartOfAccountsPage.jsx — دليل الحسابات (بالتصميم الموحّد)
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
  Box, Paper, Typography, TextField, InputAdornment, IconButton, Button, Tooltip, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import AddIcon from '@mui/icons-material/Add';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import CloudDownloadOutlinedIcon from '@mui/icons-material/CloudDownloadOutlined';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess';
import RefreshIcon from '@mui/icons-material/Refresh';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import FolderOpenOutlinedIcon from '@mui/icons-material/FolderOpenOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import BlockIcon from '@mui/icons-material/Block';
import LayersOutlinedIcon from '@mui/icons-material/LayersOutlined';
import { toast } from 'react-toastify';
import chartOfAccountsApi from '../../../api/chartOfAccountsApi';
import AddAccountModal, { AccountNature, AccountTypes } from './AddAccountModal';
import { useAuth } from '../../../context/AuthContext';
import { PERMISSIONS } from '../../../constants/permissions';
import { ReportPage, fieldSx, fmtNumber, BRAND } from '../../../components/Reports/ReportKit';

const MAX_LEVEL = 5;
const LEVEL_COLORS = ['#4F46E5', '#0284c7', '#059669', '#d97706', '#64748b'];
const isActiveOf = (n) => (typeof n.isActive === 'boolean' ? n.isActive : typeof n.IsActive === 'boolean' ? n.IsActive : true);
const nameOf = (list, id) => list.find(x => Number(x.id) === Number(id))?.name || '—';

// تصفية الشجرة: يُبقي الحساب المطابق وكل آبائه، ويرجع الآباء الواجب فتحهم
function filterTree(nodes, term) {
  const open = new Set();
  if (!term) return { nodes, open };
  const q = term.toLowerCase();
  const walk = (list) => list.reduce((acc, n) => {
    const hit = String(n.code || '').toLowerCase().includes(q)
      || String(n.nameAr || '').toLowerCase().includes(q)
      || String(n.nameEn || '').toLowerCase().includes(q);
    const kids = n.children?.length ? walk(n.children) : [];
    if (hit || kids.length) {
      if (kids.length) open.add(n.id);
      acc.push({ ...n, children: hit && !kids.length ? n.children : kids });
    }
    return acc;
  }, []);
  return { nodes: walk(nodes), open };
}

function flatten(nodes, out = []) {
  nodes.forEach(n => { out.push(n); if (n.children?.length) flatten(n.children, out); });
  return out;
}

function Highlight({ text, term }) {
  const s = String(text ?? '');
  if (!term) return s;
  const i = s.toLowerCase().indexOf(term.toLowerCase());
  if (i < 0) return s;
  return <>{s.slice(0, i)}<Box component="mark" sx={{ bgcolor: '#fef08a', color: 'inherit', borderRadius: '3px', px: .25 }}>{s.slice(i, i + term.length)}</Box>{s.slice(i + term.length)}</>;
}

const Chip = ({ children, color = '#64748b', bg = '#f1f5f9', icon }) => (
  <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: .4, px: 1, py: .15, borderRadius: '999px', fontSize: 11.5, fontWeight: 800, color, bgcolor: bg, whiteSpace: 'nowrap', '& svg': { fontSize: 13 } }}>
    {icon}{children}
  </Box>
);

export default function ChartOfAccountsPage() {
  const { hasPermission } = useAuth();
  const canManage = hasPermission(PERMISSIONS.CHART_OF_ACCOUNTS_MANAGE);

  const [tree, setTree] = useState([]);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);
  const [confirmSeed, setConfirmSeed] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState(null);

  const fetchTree = useCallback(() => {
    setLoading(true);
    chartOfAccountsApi.getTree()
      .then(res => {
        const data = Array.isArray(res.data) ? res.data : [];
        setTree(data);
        // أول تحميل: افتح المستوى الأول
        setExpanded(prev => Object.keys(prev).length ? prev : Object.fromEntries(data.map(n => [n.id, true])));
      })
      .catch(() => toast.error('تعذر تحميل دليل الحسابات'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchTree(); }, [fetchTree]);

  const all = useMemo(() => flatten(tree), [tree]);
  const byId = useMemo(() => new Map(all.map(n => [String(n.id), n])), [all]);
  const selected = selectedId != null ? byId.get(String(selectedId)) || null : null;

  const term = search.trim();
  const { nodes: shown, open: searchOpen } = useMemo(() => filterTree(tree, term), [tree, term]);
  const isOpen = (id) => (term ? searchOpen.has(id) || !!expanded[id] : !!expanded[id]);

  const stats = useMemo(() => {
    const main = all.filter(n => !n.allowPosting).length;
    return [
      { label: 'إجمالي الحسابات', value: fmtNumber(all.length), icon: <AccountTreeOutlinedIcon />, color: 'primary', highlight: true },
      { label: 'حسابات رئيسية', value: fmtNumber(main), icon: <FolderOutlinedIcon />, color: 'info' },
      { label: 'حسابات فرعية (حركة)', value: fmtNumber(all.length - main), icon: <DescriptionOutlinedIcon />, color: 'success' },
      { label: 'غير نشطة', value: fmtNumber(all.filter(n => !isActiveOf(n)).length), icon: <BlockIcon />, color: 'error' },
    ];
  }, [all]);

  const toggle = (id) => setExpanded(p => ({ ...p, [id]: !isOpen(id) }));
  const expandAll = () => setExpanded(Object.fromEntries(all.filter(n => n.children?.length).map(n => [n.id, true])));
  const collapseAll = () => { setExpanded({}); setSearch(''); };

  // مسار الحساب المحدد (للعرض)
  const path = useMemo(() => {
    if (!selected) return [];
    const out = [];
    let cur = selected;
    while (cur) { out.unshift(cur); cur = cur.parentId != null ? byId.get(String(cur.parentId)) : null; }
    return out;
  }, [selected, byId]);

  const openAdd = () => {
    if (selected && selected.level >= MAX_LEVEL) { toast.error('لا يمكن إضافة حساب تحت حساب من المستوى الأخير'); return; }
    if (selected?.allowPosting) toast.info('الحساب المحدد فرعي (عليه حركة) — تأكد أنه يصلح أن يكون أبًا');
    setAddOpen(true);
  };
  const openEdit = (node = selected) => {
    if (!node) { toast.error('اختر حسابًا أولاً للتعديل'); return; }
    setEditing(node);
  };

  const seed = async () => {
    setSeeding(true);
    try {
      await chartOfAccountsApi.seedStandard();
      toast.success('تم استيراد دليل الحسابات بنجاح');
      setExpanded({});
      fetchTree();
      setConfirmSeed(false);
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || 'حدث خطأ أثناء الاستيراد');
    } finally {
      setSeeding(false);
    }
  };

  const actions = canManage ? [
    { label: 'استيراد الدليل الافتراضي', icon: <CloudDownloadOutlinedIcon />, onClick: () => setConfirmSeed(true), disabled: seeding },
    { label: 'تعديل', icon: <EditOutlinedIcon />, onClick: () => openEdit(), disabled: !selected },
    { label: selected ? 'إضافة حساب تحت المحدد' : 'إضافة حساب', icon: <AddIcon />, onClick: openAdd, primary: true },
  ] : [];

  // ── صف في الشجرة ──
  const renderNode = (node, depth = 0) => {
    const kids = node.children || [];
    const has = kids.length > 0;
    const open = has && isOpen(node.id);
    const sel = String(selectedId) === String(node.id);
    const active = isActiveOf(node);
    const lc = LEVEL_COLORS[(node.level || 1) - 1] || LEVEL_COLORS[4];
    const FolderIcon = open ? FolderOpenOutlinedIcon : FolderOutlinedIcon;

    return (
      <Box key={node.id}>
        <Box
          role="treeitem" aria-selected={sel} aria-expanded={has ? open : undefined} tabIndex={0}
          onClick={() => setSelectedId(node.id)}
          onDoubleClick={() => { if (has) toggle(node.id); else if (canManage) openEdit(node); }}
          onKeyDown={e => {
            if (e.key === 'Enter') setSelectedId(node.id);
            if (e.key === 'ArrowLeft' && has && !open) toggle(node.id);
            if (e.key === 'ArrowRight' && has && open) toggle(node.id);
          }}
          sx={{
            position: 'relative', display: 'flex', alignItems: 'center', gap: 1, minHeight: 42,
            pr: `${8 + depth * 22}px`, pl: 1, borderRadius: '10px', cursor: 'pointer', outline: 'none',
            bgcolor: sel ? BRAND.primarySoft : 'transparent',
            boxShadow: sel ? `inset -3px 0 0 ${BRAND.primary}` : 'none',
            opacity: active ? 1 : .6,
            '&:hover': { bgcolor: sel ? BRAND.primarySoft : '#f8fafc' },
            '&:focus-visible': { boxShadow: `0 0 0 2px ${BRAND.primaryLight}` },
            '&:hover .row-actions': { opacity: 1 },
          }}
        >
          {/* خطوط الشجرة */}
          {Array.from({ length: depth }).map((_, i) => (
            <Box key={i} aria-hidden sx={{ position: 'absolute', top: 0, bottom: 0, right: `${19 + i * 22}px`, borderRight: '1px dashed #e2e8f0' }} />
          ))}
          <IconButton size="small" onClick={e => { e.stopPropagation(); toggle(node.id); }}
            sx={{ width: 26, height: 26, visibility: has ? 'visible' : 'hidden', color: '#64748b' }} aria-label={open ? 'طي' : 'فتح'}>
            <ChevronLeftIcon sx={{ fontSize: 20, transition: 'transform .15s', transform: open ? 'rotate(-90deg)' : 'none' }} />
          </IconButton>
          {node.allowPosting
            ? <DescriptionOutlinedIcon sx={{ fontSize: 19, color: '#94a3b8' }} />
            : <FolderIcon sx={{ fontSize: 20, color: lc }} />}
          <Typography sx={{ fontFamily: 'monospace', fontWeight: 800, fontSize: 13.5, color: node.level === 1 ? BRAND.primary : '#475569', minWidth: 64, direction: 'ltr', textAlign: 'right' }}>
            <Highlight text={node.code} term={term} />
          </Typography>
          <Typography sx={{ flex: 1, minWidth: 0, fontWeight: node.level <= 2 ? 800 : 600, fontSize: node.level === 1 ? 15.5 : 14.5, color: '#0f172a', textDecoration: active ? 'none' : 'line-through' }} noWrap>
            <Highlight text={node.nameAr} term={term} />
            {has && <Box component="span" sx={{ color: '#94a3b8', fontWeight: 600, fontSize: 12, mr: .75 }}>({kids.length})</Box>}
          </Typography>
          <Box sx={{ display: { xs: 'none', sm: 'flex' }, gap: .5, alignItems: 'center' }}>
            {!active && <Chip color="#be123c" bg="#ffe4e6" icon={<BlockIcon />}>غير نشط</Chip>}
            {node.allowPosting
              ? <Chip color="#047857" bg="#d1fae5">فرعي</Chip>
              : <Chip color={lc} bg={`${lc}14`}>رئيسي</Chip>}
            <Chip icon={<LayersOutlinedIcon />}>م{node.level}</Chip>
          </Box>
          {canManage && (
            <Box className="row-actions" sx={{ display: 'flex', opacity: sel ? 1 : 0, transition: 'opacity .15s' }}>
              {node.level < MAX_LEVEL && (
                <Tooltip title="إضافة حساب تحته">
                  <IconButton size="small" onClick={e => { e.stopPropagation(); setSelectedId(node.id); setAddOpen(true); }} sx={{ color: BRAND.primary }}><AddIcon fontSize="small" /></IconButton>
                </Tooltip>
              )}
              <Tooltip title="تعديل">
                <IconButton size="small" onClick={e => { e.stopPropagation(); setSelectedId(node.id); openEdit(node); }} sx={{ color: '#d97706' }}><EditOutlinedIcon fontSize="small" /></IconButton>
              </Tooltip>
            </Box>
          )}
        </Box>
        {open && kids.map(k => renderNode(k, depth + 1))}
      </Box>
    );
  };

  return (
    <ReportPage
      title="دليل الحسابات"
      subtitle="عرض شجري للحسابات الرئيسية والفرعية"
      icon={<AccountTreeOutlinedIcon />}
      actions={actions}
      stats={all.length ? stats : []}
    >
      <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr', lg: 'minmax(0,1fr) 320px' }, alignItems: 'start' }}>
        {/* الشجرة */}
        <Paper elevation={0} sx={{ borderRadius: '16px', border: '1px solid #e5e7eb', overflow: 'hidden' }}>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', p: 1.25, borderBottom: '1px solid #e5e7eb', bgcolor: '#fafbfc', flexWrap: 'wrap' }}>
            <TextField size="small" value={search} onChange={e => setSearch(e.target.value)} placeholder="بحث برقم الحساب أو الاسم..."
              sx={{ ...fieldSx, flex: '1 1 260px' }}
              InputProps={{
                startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: '#94a3b8' }} /></InputAdornment>,
                endAdornment: search ? <InputAdornment position="end"><IconButton size="small" onClick={() => setSearch('')}><CloseIcon fontSize="small" /></IconButton></InputAdornment> : null,
              }} />
            <Box sx={{ display: 'flex', gap: .5 }}>
              <Tooltip title="فتح الكل"><IconButton onClick={expandAll} sx={{ border: '1px solid #e5e7eb', borderRadius: '10px' }}><UnfoldMoreIcon /></IconButton></Tooltip>
              <Tooltip title="طي الكل"><IconButton onClick={collapseAll} sx={{ border: '1px solid #e5e7eb', borderRadius: '10px' }}><UnfoldLessIcon /></IconButton></Tooltip>
              <Tooltip title="تحديث"><span><IconButton onClick={fetchTree} disabled={loading} sx={{ border: '1px solid #e5e7eb', borderRadius: '10px' }}><RefreshIcon /></IconButton></span></Tooltip>
            </Box>
          </Box>

          <Box role="tree" sx={{ p: 1, maxHeight: { lg: '68vh' }, overflow: 'auto' }}>
            {loading ? (
              <Box sx={{ display: 'grid', placeItems: 'center', py: 10 }}><CircularProgress sx={{ color: BRAND.primary }} /></Box>
            ) : !tree.length ? (
              <Box sx={{ textAlign: 'center', py: 8, color: '#64748b' }}>
                <AccountTreeOutlinedIcon sx={{ fontSize: 52, color: '#cbd5e1' }} />
                <Typography sx={{ fontWeight: 800, mt: 1 }}>لا يوجد دليل حسابات بعد</Typography>
                {canManage && (
                  <Button onClick={() => setConfirmSeed(true)} startIcon={<CloudDownloadOutlinedIcon />} variant="contained"
                    sx={{ mt: 2, borderRadius: '10px', fontWeight: 800, background: BRAND.buttonGradient, '& .MuiButton-startIcon': { ml: .75, mr: -.25 } }}>
                    استيراد الدليل الافتراضي
                  </Button>
                )}
              </Box>
            ) : shown.length ? shown.map(n => renderNode(n)) : (
              <Typography sx={{ textAlign: 'center', py: 8, color: '#64748b' }}>لا توجد حسابات مطابقة للبحث</Typography>
            )}
          </Box>
        </Paper>

        {/* تفاصيل الحساب المحدد */}
        <Paper elevation={0} sx={{ borderRadius: '16px', border: '1px solid #e5e7eb', overflow: 'hidden', position: { lg: 'sticky' }, top: { lg: 12 } }}>
          <Box sx={{ px: 2, py: 1.25, borderBottom: '1px solid #e5e7eb', bgcolor: '#fafbfc' }}>
            <Typography sx={{ fontWeight: 800, fontSize: 15 }}>الحساب المحدد</Typography>
          </Box>
          {!selected ? (
            <Typography sx={{ p: 3, textAlign: 'center', color: '#94a3b8', fontSize: 14 }}>اختر حسابًا من الشجرة لعرض تفاصيله</Typography>
          ) : (
            <Box sx={{ p: 2, display: 'grid', gap: 1.5 }}>
              <Box>
                <Typography sx={{ fontFamily: 'monospace', fontWeight: 800, color: BRAND.primary, fontSize: 18, direction: 'ltr', textAlign: 'right' }}>{selected.code}</Typography>
                <Typography sx={{ fontWeight: 800, fontSize: 17 }}>{selected.nameAr}</Typography>
                {selected.nameEn && <Typography sx={{ color: '#64748b', fontSize: 13, direction: 'ltr', textAlign: 'right' }}>{selected.nameEn}</Typography>}
              </Box>
              <Box sx={{ display: 'flex', gap: .5, flexWrap: 'wrap' }}>
                {isActiveOf(selected) ? <Chip color="#047857" bg="#d1fae5">نشط</Chip> : <Chip color="#be123c" bg="#ffe4e6" icon={<BlockIcon />}>غير نشط</Chip>}
                {selected.allowPosting ? <Chip color="#047857" bg="#d1fae5">فرعي (عليه حركة)</Chip> : <Chip color={BRAND.primary} bg={BRAND.primarySoft}>رئيسي (تجميعي)</Chip>}
                <Chip icon={<LayersOutlinedIcon />}>المستوى {selected.level}</Chip>
              </Box>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                {[
                  ['الطبيعة', nameOf(AccountNature, selected.nature)],
                  ['النوع', nameOf(AccountTypes, selected.accountType)],
                  ['الحسابات تحته', fmtNumber(selected.children?.length || 0)],
                  ['مركز تكلفة', selected.costCenterRequired ? 'مطلوب' : 'لا'],
                ].map(([l, v]) => (
                  <Box key={l} sx={{ p: 1, borderRadius: '10px', bgcolor: '#f8fafc', border: '1px solid #eef0f4' }}>
                    <Typography sx={{ fontSize: 11.5, color: '#94a3b8', fontWeight: 700 }}>{l}</Typography>
                    <Typography sx={{ fontSize: 14, fontWeight: 800 }}>{v}</Typography>
                  </Box>
                ))}
              </Box>
              {path.length > 1 && (
                <Box>
                  <Typography sx={{ fontSize: 12, color: '#94a3b8', fontWeight: 700, mb: .5 }}>المسار</Typography>
                  <Box sx={{ display: 'grid', gap: .25 }}>
                    {path.map((p, i) => (
                      <Typography key={p.id} onClick={() => setSelectedId(p.id)} sx={{ fontSize: 13, pr: i * 1.25, cursor: 'pointer', color: i === path.length - 1 ? BRAND.primary : '#475569', fontWeight: i === path.length - 1 ? 800 : 600, '&:hover': { textDecoration: 'underline' } }}>
                        {p.code} — {p.nameAr}
                      </Typography>
                    ))}
                  </Box>
                </Box>
              )}
              {canManage && (
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button fullWidth variant="contained" startIcon={<AddIcon />} onClick={openAdd} disabled={selected.level >= MAX_LEVEL}
                    sx={{ borderRadius: '10px', fontWeight: 800, background: BRAND.buttonGradient, '& .MuiButton-startIcon': { ml: .75, mr: -.25 } }}>إضافة تحته</Button>
                  <Button fullWidth variant="outlined" startIcon={<EditOutlinedIcon />} onClick={() => openEdit()}
                    sx={{ borderRadius: '10px', fontWeight: 800, color: '#b45309', borderColor: '#fcd34d', '& .MuiButton-startIcon': { ml: .75, mr: -.25 } }}>تعديل</Button>
                </Box>
              )}
            </Box>
          )}
        </Paper>
      </Box>

      {/* تأكيد استيراد الدليل */}
      <Dialog open={confirmSeed} onClose={() => !seeding && setConfirmSeed(false)} dir="rtl" PaperProps={{ sx: { borderRadius: '16px' } }}>
        <DialogTitle sx={{ fontWeight: 800 }}>استيراد دليل الحسابات الافتراضي</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: '#475569' }}>سيتم إضافة دليل الحسابات القياسي للشركة. قد يستغرق ذلك بعض الوقت. هل تريد المتابعة؟</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmSeed(false)} disabled={seeding} sx={{ fontWeight: 700, color: '#64748b' }}>إلغاء</Button>
          <Button variant="contained" onClick={seed} disabled={seeding} startIcon={seeding ? <CircularProgress size={16} color="inherit" /> : <CloudDownloadOutlinedIcon />}
            sx={{ borderRadius: '10px', fontWeight: 800, background: BRAND.buttonGradient, '& .MuiButton-startIcon': { ml: .75, mr: -.25 } }}>
            {seeding ? 'جاري الاستيراد...' : 'استيراد'}
          </Button>
        </DialogActions>
      </Dialog>

      <AddAccountModal isOpen={addOpen} onClose={() => setAddOpen(false)} onSuccess={fetchTree} initialParentId={selected?.id ?? ''} />
      <AddAccountModal isOpen={Boolean(editing)} onClose={() => setEditing(null)} onSuccess={fetchTree} initialAccount={editing} />
    </ReportPage>
  );
}